import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import NotificationComposer from './components/NotificationComposer';
import NotificationHistory from './components/NotificationHistory';
import NotificationAnalytics from './components/NotificationAnalytics';
import notificationsService from '../../services/notificationsService';
import { useAuth } from '../../contexts/AuthContext';
import { useArtistData } from '../../hooks/useArtistData';

const ArtistNotificationsManager = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { artistData } = useArtistData();
  const [activeTab, setActiveTab] = useState('compose');
  const [notifications, setNotifications] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract artist brand colors for theming
  const highlightColor = artistData?.profile?.highlight_color || artistData?.profile?.highlightColor || '#6366F1';
  const secondaryColor = artistData?.profile?.secondary_color || artistData?.profile?.secondaryColor || '#6366F1';

  // Sections menu configuration
  const sections = [
    {
      id: 'profile',
      name: 'Profile',
      icon: 'User',
      description: 'Basic information and contact details',
      required: true,
      completed: !!(artistData?.profile?.name && artistData?.profile?.email),
      route: '/artist-content-management'
    },
    {
      id: 'promotion',
      name: 'Promotion',
      icon: 'Megaphone',
      description: 'App images and featured content',
      required: true,
      completed: !!(artistData?.promotion?.splashImage && artistData?.promotion?.logo),
      route: '/artist-content-management'
    },
    {
      id: 'about',
      name: 'About',
      icon: 'FileText',
      description: 'Biography and booking information',
      required: true,
      completed: !!(artistData?.about?.aboutText && artistData?.about?.bookingContact?.name),
      route: '/artist-content-management'
    },
    {
      id: 'calendar',
      name: 'Calendar',
      icon: 'Calendar',
      description: 'Events and performances',
      required: false,
      completed: artistData?.calendar?.length > 0,
      route: '/artist-content-management'
    },
    {
      id: 'work',
      name: 'Work',
      icon: 'Briefcase',
      description: 'Portfolio and projects',
      required: false,
      completed: artistData?.work?.length > 0,
      route: '/artist-content-management'
    },
    {
      id: 'shop',
      name: 'Shop',
      icon: 'ShoppingCart',
      description: 'Merchandise and products',
      required: false,
      completed: Object.values(artistData?.shop || {})?.some(Boolean),
      route: '/artist-content-management'
    },
    {
      id: 'metrics',
      name: 'Metrics',
      icon: 'BarChart3',
      description: 'Mobile app analytics and insights',
      required: false,
      completed: false,
      route: '/artist-metrics-dashboard'
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: 'Bell',
      description: 'Push notifications to mobile users',
      required: false,
      completed: false,
      active: true
    }
  ];

  const tabs = [
    {
      id: 'compose',
      name: 'Compose',
      icon: 'PenTool',
      description: 'Create new notifications'
    },
    {
      id: 'history',
      name: 'History',
      icon: 'History',
      description: 'View sent notifications'
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: 'BarChart3',
      description: 'Performance metrics'
    }
  ];

  // Handle section navigation
  const handleSectionClick = (section) => {
    if (section?.route) {
      navigate(section?.route);
    }
  };

  // Load notifications and analytics
  const loadData = async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      const [notificationsResult, analyticsResult] = await Promise.all([
        notificationsService?.getNotifications(user?.id),
        notificationsService?.getNotificationAnalytics(user?.id)
      ]);

      if (notificationsResult?.error) throw new Error(notificationsResult.error);
      if (analyticsResult?.error) throw new Error(analyticsResult.error);

      setNotifications(notificationsResult?.data || []);
      setAnalytics(analyticsResult?.data);
    } catch (err) {
      setError(err?.message || 'Failed to load notifications data');
    } finally {
      setLoading(false);
    }
  };

  // Handle notification creation
  const handleNotificationCreated = (newNotification) => {
    setNotifications(prev => [newNotification, ...prev]);
    setActiveTab('history'); // Switch to history tab to show the new notification
  };

  // Handle notification sent
  const handleNotificationSent = async (notificationId) => {
    try {
      const result = await notificationsService?.sendNotification(notificationId);
      if (result?.error) throw new Error(result.error);

      // Update the notification status in local state
      setNotifications(prev => 
        prev?.map(notif => 
          notif?.id === notificationId 
            ? { ...notif, status: 'sent', sent_at: new Date()?.toISOString() }
            : notif
        )
      );

      // Reload analytics to reflect the change
      const analyticsResult = await notificationsService?.getNotificationAnalytics(user?.id);
      if (analyticsResult?.data) {
        setAnalytics(analyticsResult?.data);
      }
    } catch (err) {
      setError(err?.message || 'Failed to send notification');
    }
  };

  // Handle notification deletion
  const handleNotificationDeleted = (notificationId) => {
    setNotifications(prev => prev?.filter(notif => notif?.id !== notificationId));
  };

  useEffect(() => {
    loadData();
  }, [user?.id]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user?.id) return;

    const subscription = notificationsService?.subscribeToNotifications(user?.id, (payload) => {
      // Refresh data when notifications are updated
      loadData();
    });

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, [user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-text-secondary">Loading notifications...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Section Navigation */}
            <div className="lg:col-span-1">
              <div
                className="bg-surface rounded-lg border p-4 sticky top-24"
                style={{ borderColor: `${highlightColor}30` }}
              >
                <div className="flex items-center space-x-2 mb-4">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: highlightColor }}
                  />
                  <h3 className="text-lg font-semibold text-text-primary">Sections</h3>
                </div>
                
                <nav className="space-y-2">
                  {sections?.map((section) => (
                    <button
                      key={section?.id}
                      onClick={() => handleSectionClick(section)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                        section?.active
                          ? 'text-white' :'hover:bg-muted text-text-primary'
                      }`}
                      style={section?.active ? {
                        background: `linear-gradient(135deg, ${highlightColor}E6, ${secondaryColor}CC)`,
                        boxShadow: `0 2px 8px ${highlightColor}40`
                      } : {}}
                    >
                      <div className="relative">
                        <Icon 
                          name={section?.icon} 
                          size={18} 
                          className={section?.active ? 'text-white' : 'text-text-secondary'}
                        />
                        {section?.completed && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full flex items-center justify-center">
                            <Icon name="Check" size={8} color="white" />
                          </div>
                        )}
                        {section?.route && !section?.active && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full flex items-center justify-center">
                            <Icon name="ExternalLink" size={6} color="white" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{section?.name}</span>
                          {section?.required && (
                            <span
                              className="text-xs"
                              style={{ color: section?.active ? 'rgba(255,255,255,0.8)' : secondaryColor }}
                            >*</span>
                          )}
                        </div>
                        <p className="text-xs opacity-70 truncate">{section?.description}</p>
                      </div>
                    </button>
                  ))}
                </nav>

                {/* Color accent strip at bottom of nav */}
                <div
                  className="mt-4 h-px rounded-full opacity-40"
                  style={{ background: `linear-gradient(to right, ${highlightColor}, ${secondaryColor}, transparent)` }}
                />

                {/* Artist Info */}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${highlightColor}, ${secondaryColor})` }}
                    >
                      <Icon name="Palette" size={16} color="white" />
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">{artistData?.name || userProfile?.full_name || 'Artist'}</p>
                      <p className="text-xs text-text-secondary capitalize">{artistData?.artistType || userProfile?.artist_type || 'visual'} Artist</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3">
              {/* Header Section */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <div className="flex items-center space-x-3 mb-1">
                    <div
                      className="w-1 h-8 rounded-full"
                      style={{ background: `linear-gradient(to bottom, ${highlightColor}, ${secondaryColor})` }}
                    />
                    <h1 className="text-3xl font-bold text-text-primary">Push Notifications</h1>
                  </div>
                  <p className="text-text-secondary mt-1 ml-4">
                    Send announcements and updates to your mobile app users
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <Button
                    variant="outline"
                    onClick={loadData}
                    iconName="RefreshCw"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-red-600" />
                    <p className="text-red-800">{error}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setError(null)}
                      iconName="X"
                      iconSize={14}
                    />
                  </div>
                </div>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-surface rounded-lg border border-border p-6">
                  <div className="flex items-center space-x-3 mb-2">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${highlightColor}20` }}
                    >
                      <Icon name="Send" size={16} style={{ color: highlightColor }} />
                    </div>
                    <div className="text-2xl font-bold text-text-primary">
                      {analytics?.totalSent || 0}
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary">Total Sent</p>
                </div>

                <div className="bg-surface rounded-lg border border-border p-6">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <Icon name="CheckCircle" size={16} className="text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-text-primary">
                      {analytics?.totalDelivered || 0}
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary">Delivered</p>
                </div>

                <div className="bg-surface rounded-lg border border-border p-6">
                  <div className="flex items-center space-x-3 mb-2">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${secondaryColor}20` }}
                    >
                      <Icon name="TrendingUp" size={16} style={{ color: secondaryColor }} />
                    </div>
                    <div className="text-2xl font-bold text-text-primary">
                      {analytics?.deliveryRate || 0}%
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary">Delivery Rate</p>
                </div>

                <div className="bg-surface rounded-lg border border-border p-6">
                  <div className="flex items-center space-x-3 mb-2">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${highlightColor}15` }}
                    >
                      <Icon name="Bell" size={16} style={{ color: highlightColor }} />
                    </div>
                    <div className="text-2xl font-bold text-text-primary">
                      {notifications?.filter(n => n?.status === 'draft')?.length || 0}
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary">Drafts</p>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="bg-surface rounded-lg border border-border mb-8">
                <div className="flex">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 p-4 font-medium transition-colors ${
                        activeTab === tab?.id
                          ? 'text-white border-b-2' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                      }`}
                      style={activeTab === tab?.id ? {
                        background: `linear-gradient(135deg, ${highlightColor}E6, ${secondaryColor}CC)`,
                        borderBottomColor: highlightColor
                      } : {}}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="bg-surface rounded-lg border border-border">
                {activeTab === 'compose' && (
                  <NotificationComposer 
                    onNotificationCreated={handleNotificationCreated}
                    artistId={user?.id}
                  />
                )}
                
                {activeTab === 'history' && (
                  <NotificationHistory 
                    notifications={notifications}
                    onNotificationSent={handleNotificationSent}
                    onNotificationDeleted={handleNotificationDeleted}
                  />
                )}
                
                {activeTab === 'analytics' && (
                  <NotificationAnalytics 
                    analytics={analytics}
                    notifications={notifications}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistNotificationsManager;